import React, { useCallback, useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useSnackbar } from '@goodfynd/context';
import { strings } from '@goodfynd/strings';
import type { IconName } from '@goodfynd/ui';
import { IconButton } from '@goodfynd/ui';

import { useWebShare } from '../../hooks/useWebShare';

import { StyledButton } from './styles';
import type { ShareButtonProps } from './types';

const getDefaultUrl = () =>
  typeof window !== 'undefined' ? window.location.href : '';

const getShareIcon = (): IconName => {
  if (typeof navigator === 'undefined') {
    return 'share_android';
  }

  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
    ? 'share_ios'
    : 'share_android';
};

export default function ShareButton({
  buttonStyle = 'default',
  text,
  title,
  url,
  ...props
}: ShareButtonProps) {
  const { openSnackbar } = useSnackbar();
  const [success, setSuccess] = useState(false);
  const { busy, isSupported, share } = useWebShare();

  const resolvedUrl = url ?? getDefaultUrl();

  const handleClick = useCallback(
    async (e: ClickButtonEvent) => {
      if (!isSupported) {
        return e.preventDefault();
      }

      share({ text, title, url: resolvedUrl });
    },
    [isSupported, resolvedUrl, share, text, title]
  );

  const handleCopy = useCallback(() => {
    openSnackbar({
      message: strings.labels.copiedToClipboard,
      type: 'success',
    });

    setSuccess(true);
    setTimeout(() => {
      setSuccess(false);
    }, 1000);
  }, [openSnackbar]);

  const commonProps = useMemo(
    () => ({
      disabled: busy,
      onClick: handleClick,
      ...props,
    }),
    [busy, handleClick, props]
  );

  const renderButton = useCallback(() => {
    const iconName: IconName = success ? 'check' : getShareIcon();

    switch (buttonStyle) {
      case 'default':
        return (
          <StyledButton
            busy={busy}
            color="neutral"
            iconLeft={iconName}
            size="small"
            {...commonProps}
          >
            {strings.labels.share}
          </StyledButton>
        );

      case 'icon':
        return (
          <IconButton
            busy={busy}
            color="neutral"
            icon={iconName}
            {...commonProps}
          />
        );
    }
  }, [busy, buttonStyle, commonProps, success]);

  return isSupported ? (
    renderButton()
  ) : (
    <CopyToClipboard onCopy={handleCopy} text={resolvedUrl}>
      {renderButton()}
    </CopyToClipboard>
  );
}
